<template>
  <ion-page>
    <ion-content>
      <the-header :title="t('test')" :show-avatar="false" />
      <div class="test-view-page__share-btn" @click="handleShare">
        <IonIcon :icon="shareSocial" />
      </div>
      <div v-if="state.test" class="test-view-page__header">
        <div
          class="test-view-page__img-container"
          :style="{ backgroundImage: cssImagePath }"
        >
          <!-- <img class="test-view-page__img" :src="state.test.imgPath" /> -->
          <div class="test-view-page__category">{{ themeName }}</div>
        </div>
      </div>
      <div class="test-view-page content-container">
        <template v-if="state.test">
          <div class="test-view-page__meta">
            <div class="test-view-page__meta-item">
              <ion-icon :icon="alarm" />
              <div>{{ state.test.approxTime }}</div>
            </div>
            <div class="test-view-page__meta-item">
              <ion-icon :icon="book" />
              <div>
                {{ t('questionsCount', { n: state.test.questions.length }) }}
              </div>
            </div>
          </div>
          <div class="test-view-page__title">{{ state.test.title }}</div>
          <div class="test-view-page__description">
            {{ state.test.description }}
          </div>
          <div class="test-view-page__benefits">
            <div class="test-view-page__benefits-title">
              {{ t('testBenefitsTitle') }}
            </div>
            <div
              class="test-view-page__benefits-item"
              v-for="(benefit, i) in state.test.benefits"
              :key="i"
            >
              {{ benefit }}
            </div>
          </div>

          <div
            v-if="state.relatedCourses.length"
            class="test-view-page__related-courses"
          >
            <div class="test-view-page__related-courses-title">
              {{ t('relatedCourses') }}
            </div>
            <div class="test-view-page__related-courses-list">
              <swiper
                class="tests-bar"
                :slides-per-view="1.8"
                :grid="{
                  rows: 1,
                  fill: 'row',
                }"
                :space-between="10"
                :modules="[Grid]"
              >
                <swiper-slide
                  v-for="course in state.relatedCourses"
                  :key="course.id"
                  class="tests-bar__item clickable"
                >
                  <course-item
                    :course="course"
                    mini
                    @click="handleCourseClick(course)"
                  />
                </swiper-slide>
              </swiper>
            </div>
          </div>

          <div class="test-view-page__disclaimer">
            <span>{{ t('disclaimer') }}: </span>
            <div style="display: inline" v-html="t('testDisclaimer')" />
          </div>
          <br />
          <base-button
            class="test-view-page__btn"
            @click="handleGetStartedClick"
            >{{ t('getStarted') }}</base-button
          >
        </template>
        <div class="spinner" v-else>
          <IonSpinner />
        </div>
      </div>
      <share-modal
        :is-opened="state.isShareModalOpened"
        type="test"
        :test="state.test || undefined"
        @close="state.isShareModalOpened = false"
      />
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import {
  IonContent,
  IonPage,
  IonIcon,
  IonSpinner,
  onIonViewWillEnter,
} from '@ionic/vue';

import { alarm, book, shareSocial } from 'ionicons/icons';
import { computed, reactive } from 'vue';
import { CourseI, TestItemI, ThemeI } from '@/types';
import { useRoute, useRouter } from 'vue-router';
import api from '@/api';
import { useInternalization } from '@/composables/useInternalization';

import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';
import 'swiper/css/grid';
import { Grid } from 'swiper/modules';

import CourseItem from '@/components/CourseItem.vue';
import TheHeader from '@/components/TheHeader.vue';
import BaseButton from '@/components/BaseButton.vue';
import ShareModal from '@/components/modals/ShareModal.vue';
import { logEvent } from '@/services/analytics';

const state = reactive<{
  test: TestItemI | null;
  theme: ThemeI | null;
  relatedCourses: CourseI[];
  isShareModalOpened: boolean;
}>({
  test: null,
  theme: null,
  relatedCourses: [],
  isShareModalOpened: false,
});

const route = useRoute();
const router = useRouter();
const { t, locale } = useInternalization();

const testId = computed(() => {
  return route.params.id as string;
});
const themeName = computed(() => {
  return (
    state.theme?.translations[locale.value]?.name || state.theme?.name || ''
  );
});
const cssImagePath = computed(() => {
  if (state.test) {
    return 'url("' + state.test.imgPath + '")';
  }
  return '';
});

const handleShare = () => {
  if (!state.test) {
    return;
  }
  state.isShareModalOpened = true;
};

const handleGetStartedClick = async () => {
  logEvent('test_click_get_started');

  if (state.test) {
    router.push({
      name: 'TestInProgress',
      params: {
        id: state.test.id,
      },
    });
  }
};

const getTest = async () => {
  state.test = await api.tests.getById(testId.value);
};
const getThemeById = async (id: string) => {
  state.theme = await api.themes.getById(id);
};
const getRelatedCourses = async () => {
  if (!state.test?.relatedCourseIds) {
    return;
  }

  state.relatedCourses = [];

  await Promise.all(
    state.test.relatedCourseIds.map(async (id) => {
      const course = await api.courses.getById(id);
      if (course) {
        state.relatedCourses.push(course);
      }
    })
  );
};

const handleCourseClick = (course: CourseI) => {
  router.push({ name: 'TherapyCourse', params: { id: course.id } });
};

onIonViewWillEnter(async () => {
  await getTest();
  if (state.test) {
    await getThemeById(state.test.categoryId);
  }
  await getRelatedCourses();
});
</script>

<style lang="scss" scoped>
.test-view-page {
  margin-bottom: 6em;
  max-width: 600px !important;
  color: #172659;
  padding-top: 2em;

  @media (max-width: 600px) {
    padding-top: calc(100vw - (70px + var(--ion-safe-area-top)));

    &__img-container {
      max-width: 600px !important;
      max-height: 600px !important;
      top: 0;
      border-radius: 0px !important;
    }

    &__header {
      position: absolute;
      top: 0;
      z-index: -1;
      width: 100%;
    }
    &__share-btn {
      right: 8px;
    }
  }

  &__related-courses {
    margin-top: 2em;

    &-title {
      font-size: 18px;
      font-weight: 600;
      line-height: 22px;
      margin-bottom: 1em;
    }
  }

  p {
    margin-bottom: 1em;
  }

  &__img-container {
    position: relative;
    max-width: 300px;
    max-height: 300px;
    background-repeat: no-repeat;
    background-size: contain;
    width: 100%;
    aspect-ratio: 1/1;
    height: auto;
    margin: 0 auto;
    border-radius: 20px;
  }
  &__share-btn {
    position: absolute;
    cursor: pointer;
    z-index: 1;
    right: 15px;
    top: calc(22px + var(--ion-safe-area-top));
    width: 40px;
    height: 40px;
    background: #ffffff40;
    box-shadow: 0px 4px 8px 0px #37226014;
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.4rem;
  }

  &__category {
    padding: 8px 18px;
    background: #f9daf8;
    color: #172659;
    // box-shadow: 0px 2px 5px 0px #2323230d;
    // margin-bottom: 1.5em;
    width: fit-content;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 500;
    position: absolute;
    bottom: 16px;
    left: 16px;
  }
  &__title {
    font-size: 22px;
    font-weight: 600;
    line-height: 26px;
    letter-spacing: 0em;
    text-align: left;
    color: #172659;
    margin-bottom: 1em;
  }
  &__meta {
    display: flex;
    align-items: center;
    margin-bottom: 1em;
    color: #6288ff;
    font-size: 0.8rem;

    &-item {
      display: flex;
      align-items: center;
      ion-icon {
        margin-right: 5px;
      }
      &:not(:last-child) {
        margin-right: 8px;
      }
    }
  }
  &__description {
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
    text-align: left;
    white-space: pre-wrap;
    margin-bottom: 1em;
  }
  &__disclaimer {
    margin-top: 2em;
    font-size: 15px;
    font-weight: 400;
    line-height: 20px;
    text-align: left;
    span {
      font-weight: 600;
    }
  }
  &__benefits {
    background: #fff;
    padding: 16px;
    border-radius: 20px;
    &-title {
      font-size: 18px;
      font-weight: 600;
      line-height: 22px;
      margin-bottom: 1em;
    }
    &-item {
      font-size: 15px;
      line-height: 20px;
      position: relative;
      padding-left: 16px;

      &::before {
        content: '';
        position: absolute;
        width: 8px;
        height: 8px;
        border-radius: 100%;
        background: #6289ff;
        left: 0;
        top: 50%;
        transform: translate(-50%, -50%);
      }

      &:not(:last-child) {
        margin-bottom: 12px;
      }
    }
  }
}
.spinner {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -70px;
}
:deep(.header__title) {
  background: #ffffff40 !important;
  box-shadow: 0px 4px 8px 0px #37226014 !important;
  padding: 0px 12px !important;
  border-radius: 20px !important;
  height: 40px !important;
  display: flex;
  align-items: center;
  justify-content: center;
}
:deep(.header__back-button) {
  background: #ffffff40 !important;
  box-shadow: 0px 4px 8px 0px #37226014 !important;
  border-radius: 100% !important;
  width: 40px !important;
  height: 40px !important;
}
</style>
